import clsx from "clsx";
import { Check, X } from "lucide-react";
import { Dialog as RDialog, DropdownMenu, Popover } from "radix-ui";
import { createContext, useContext, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

/*
 * Overlays on Radix primitives: they bring focus trapping, Escape to close,
 * scroll locking and the ARIA roles, so our components only add the look.
 */

const OVERLAY = "fixed inset-0 z-40 bg-overlay";

/**
 * Inside a dialog or sheet: the rest of the panel (the assistant's chat
 * among it) cannot be reached while it is open, so nothing offers to go there.
 */
const InOverlay = createContext(false);
export const useInOverlay = (): boolean => useContext(InOverlay);

/** Centered modal; on phones it fills the bottom of the screen like a sheet. */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  wide = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const { t } = useTranslation();
  // A confirmation that says everything in its description has no body: no empty band.
  const hasBody = children !== undefined && children !== null && children !== false;
  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal>
        <RDialog.Overlay className={OVERLAY} />
        <RDialog.Content
          className={clsx(
            "fixed inset-x-0 bottom-0 z-50 flex max-h-[90dvh] flex-col rounded-t-2xl border border-line bg-surface shadow-xl",
            "sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:w-full sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl",
            wide ? "sm:max-w-2xl" : "sm:max-w-lg",
          )}
          {...(description ? {} : { "aria-describedby": undefined })}
        >
          <div
            className={clsx(
              "flex items-start justify-between gap-4 px-5 py-4",
              hasBody && "border-b border-line",
            )}
          >
            <div className="flex flex-col gap-1">
              <RDialog.Title className="text-base font-semibold">{title}</RDialog.Title>
              {description && (
                <RDialog.Description className="text-sm text-muted">
                  {description}
                </RDialog.Description>
              )}
            </div>
            <RDialog.Close
              className="rounded-md p-1 text-muted hover:bg-surface-2 hover:text-text"
              aria-label={t("common.close")}
            >
              <X className="size-5" aria-hidden />
            </RDialog.Close>
          </div>
          {hasBody && (
            <InOverlay.Provider value>
              <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
            </InOverlay.Provider>
          )}
          {footer && (
            <div className="flex flex-wrap justify-end gap-2 border-t border-line px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
              {footer}
            </div>
          )}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}

/** Panel sliding in from the side (help, details); full width on phones. */
export function Sheet({
  open,
  onOpenChange,
  title,
  children,
  side = "right",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  children: ReactNode;
  side?: "left" | "right";
}) {
  const { t } = useTranslation();
  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal>
        <RDialog.Overlay className={OVERLAY} />
        <RDialog.Content
          aria-describedby={undefined}
          className={clsx(
            "fixed inset-y-0 z-50 flex w-full max-w-md flex-col border-line bg-surface shadow-xl",
            side === "right" ? "right-0 border-l" : "left-0 border-r",
          )}
        >
          <div className="flex items-center justify-between gap-4 border-b border-line px-5 py-4 pt-[max(1rem,env(safe-area-inset-top))]">
            <RDialog.Title className="text-base font-semibold">{title}</RDialog.Title>
            <RDialog.Close
              className="rounded-md p-1 text-muted hover:bg-surface-2 hover:text-text"
              aria-label={t("common.close")}
            >
              <X className="size-5" aria-hidden />
            </RDialog.Close>
          </div>
          <InOverlay.Provider value>
            <div className="flex-1 overflow-y-auto px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
              {children}
            </div>
          </InOverlay.Provider>
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}

export interface MenuItem {
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
  /** Why the item is disabled; shown next to it instead of hiding the action. */
  reason?: string;
  /** A short note under the label, shown whether or not the item is enabled. */
  hint?: string;
  /** An on/off item (`menuitemcheckbox`): a check shows when on. */
  checked?: boolean;
  /** Extra classes (a toolbar's overflow menu hides items the bar shows at that width). */
  className?: string;
}

/** Dropdown menu for row and page actions; groups are separated by a line. */
export function Menu({
  trigger,
  groups,
  align = "end",
}: {
  trigger: ReactNode;
  groups: MenuItem[][];
  align?: "start" | "end";
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align={align}
          sideOffset={4}
          collisionPadding={8}
          className="z-50 min-w-52 max-w-[calc(100vw-1rem)] rounded-xl border border-line bg-surface p-1 text-sm shadow-lg"
        >
          {groups.map((items, g) => (
            <div key={g}>
              {g > 0 && <DropdownMenu.Separator className="my-1 h-px bg-line" />}
              {items.map((item) => {
                const className = clsx(
                  "flex cursor-pointer select-none items-start gap-2 rounded-lg px-3 py-2 outline-none data-[disabled]:cursor-not-allowed data-[highlighted]:bg-surface-2",
                  item.danger ? "text-danger" : "text-text",
                  item.disabled && "opacity-60",
                  item.className,
                );
                const content = (
                  <>
                    {item.icon && <span className="mt-0.5 shrink-0">{item.icon}</span>}
                    <span className="flex flex-1 flex-col">
                      <span>{item.label}</span>
                      {item.hint && <span className="text-xs text-muted">{item.hint}</span>}
                      {item.disabled && item.reason && (
                        <span className="text-xs text-muted">{item.reason}</span>
                      )}
                    </span>
                  </>
                );
                return item.checked === undefined ? (
                  <DropdownMenu.Item
                    key={item.label}
                    disabled={item.disabled ?? false}
                    onSelect={item.onSelect}
                    className={className}
                  >
                    {content}
                  </DropdownMenu.Item>
                ) : (
                  <DropdownMenu.CheckboxItem
                    key={item.label}
                    checked={item.checked}
                    disabled={item.disabled ?? false}
                    onSelect={item.onSelect}
                    className={className}
                  >
                    {content}
                    <DropdownMenu.ItemIndicator className="mt-0.5 shrink-0 text-accent-strong">
                      <Check className="size-4" aria-hidden />
                    </DropdownMenu.ItemIndicator>
                  </DropdownMenu.CheckboxItem>
                );
              })}
            </div>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/** Small explanation bubble; opens on click so it also works on touch screens. */
export function InfoPopover({ trigger, children }: { trigger: ReactNode; children: ReactNode }) {
  return (
    <Popover.Root>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="top"
          align="start"
          sideOffset={6}
          collisionPadding={12}
          className="z-50 max-w-xs rounded-lg border border-line bg-surface px-3 py-2 text-sm text-text shadow-lg"
        >
          {children}
          <Popover.Arrow className="fill-surface" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
